/**
 * "Договорённости" card — follow-up commitments made during the call (callbacks,
 * meetings, emails, tasks). Shown verbatim from the analysis so the user can
 * see what the LLM detected. CRM activity creation is a separate delivery
 * action and does not affect this display.
 */

import { useTranslation } from 'react-i18next';
import { AnalysisSection, Tag, type XMLAnalysisResult } from './analysis-utils';

interface AgreementsSectionProps {
  result: XMLAnalysisResult;
}

/** Human-readable due date: a resolved ISO instant if present, else the spoken hint. */
function formatDue(
  dueAt: string | undefined,
  dueHint: string | undefined,
  locale: string
): string | undefined {
  if (dueAt) {
    const ts = new Date(dueAt).getTime();
    if (!Number.isNaN(ts)) {
      return new Date(ts).toLocaleString(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    }
  }
  return dueHint || undefined;
}

export function AgreementsSection({ result }: AgreementsSectionProps) {
  const { t, i18n } = useTranslation('records');

  const agreements = result.agreements;
  if (!agreements || agreements.length === 0) return null;

  const typeLabel = (type: string): string =>
    t(`records.analysisPanel.agreements.type.${type}`, type);

  return (
    <AnalysisSection
      title={t('records.analysisPanel.agreements.title', 'Домовленості')}
      summary={
        <span className="font-mono text-2xs text-fg-subtle">{agreements.length}</span>
      }
    >
      <div className="flex flex-col gap-2">
        {agreements.map((a, idx) => {
          const due = formatDue(a.dueAt, a.dueHint, i18n.language);
          return (
            <div
              key={idx}
              className="flex flex-col gap-1 rounded-md bg-bg-surface p-2.5 text-xs"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Tag variant="neutral" className="text-micro py-0">
                  {typeLabel(a.type)}
                </Tag>
                <span className="flex-1 font-medium text-fg">{a.subject}</span>
                {typeof a.confidence === 'number' && (
                  <span className="font-mono text-micro text-fg-subtle">
                    {Math.round(a.confidence * 100)}%
                  </span>
                )}
              </div>
              {a.description && <p className="text-fg-secondary">{a.description}</p>}
              <div className="flex flex-wrap items-center gap-3 text-fg-muted">
                {due && (
                  <span>
                    {t('records.analysisPanel.agreements.due', 'Термін')}: {due}
                  </span>
                )}
                {a.responsibleHint && (
                  <span>
                    {t('records.analysisPanel.agreements.responsible', 'Відповідальний')}:{' '}
                    {a.responsibleHint}
                  </span>
                )}
              </div>
              {a.evidenceQuote && (
                <p className="italic text-fg-subtle">«{a.evidenceQuote}»</p>
              )}
            </div>
          );
        })}
      </div>
    </AnalysisSection>
  );
}
